import * as THREE from 'three';

const TAU = Math.PI * 2;
/** Shared translucent surface treatment for combat powers. Every clock is supplied by the buffered scene. */
export function effectMaterial(color: number, accent = 0xffffff, mode = 0, warp = 0) {
  return new THREE.ShaderMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending, toneMapped: false,
    uniforms: { uTime: { value: 0 }, uOpacity: { value: 1 }, uProgress: { value: 0 }, uMode: { value: mode },
      uWarp: { value: warp }, uColor: { value: new THREE.Color(color) }, uAccent: { value: new THREE.Color(accent) } },
    vertexShader: `
      uniform float uTime; uniform float uWarp;
      varying vec2 vUv; varying vec3 vNormal; varying vec3 vView;
      void main() {
        vUv = uv;
        vec3 p = position + normal * uWarp * sin(position.y * 23.0 + uTime * 17.0) * sin(position.x * 19.0 - uTime * 11.0);
        vec4 view = modelViewMatrix * vec4(p, 1.0);
        vView = -view.xyz; vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * view;
      }`,
    fragmentShader: `
      uniform float uTime; uniform float uOpacity; uniform float uProgress; uniform float uMode;
      uniform vec3 uColor; uniform vec3 uAccent;
      varying vec2 vUv; varying vec3 vNormal; varying vec3 vView;
      void main() {
        float rim = pow(1.0 - abs(dot(normalize(vNormal), normalize(vView))), 3.0);
        float edge = pow(abs(vUv.y * 2.0 - 1.0), 9.0);
        float thread = pow(max(0.0, sin(vUv.x * 63.0 - uTime * 12.0 + sin(vUv.y * 17.0 + uTime * 3.0))), 20.0);
        float end = smoothstep(0.0, .05, vUv.x) * (1.0 - smoothstep(.92, 1.0, vUv.x));
        float alpha = (.025 + edge * .48 + thread * .2 + rim * .14) * end;
        if (uMode > .5 && uMode < 1.5) {
          float upward = pow(max(0.0, sin(vUv.y * 34.0 - uTime * 7.0 + sin(vUv.x * 38.0) * 1.4)), 16.0);
          alpha = (.018 + rim * .22 + upward * .17) * sin(vUv.y * 3.14159);
        }
        if (uMode > 1.5 && uMode < 2.5) {
          float pulse = exp(-pow((vUv.x - uProgress) * 12.0, 2.0));
          alpha = end * (.045 + edge * .18 + pulse * (.55 + thread * .25));
        }
        if (uMode > 2.5) alpha = .18 + rim * .35 + thread * .25;
        vec3 color = mix(uColor, uAccent, clamp(thread + edge * .45 + rim * .5, 0.0, 1.0));
        if (uMode > 3.5) {
          float core = exp(-pow((vUv.y - .5) * 7.0, 2.0));
          float pulse = exp(-pow((vUv.x - uProgress) * 9.0, 2.0));
          float surge = .82 + .18 * sin(uTime * 91.0 + vUv.x * 31.0);
          alpha = (core * .85 + thread * .22 + rim * .25) * surge;
          color = mix(uColor, uAccent, core) * (1.2 + core * 1.7 + pulse * 1.5);
        }
        gl_FragColor = vec4(color, alpha * uOpacity);
      }`,
  });
}

export function shadeEffect(root: THREE.Object3D, time: number, opacity: number, progress = 0, ppm = 40) {
  root.traverse(node => {
    if (!(node instanceof THREE.Mesh || node instanceof THREE.Points)) return;
    for (const material of [node.material].flat()) if (material instanceof THREE.ShaderMaterial) {
      if (material.uniforms.uTime) material.uniforms.uTime.value = time;
      if (material.uniforms.uOpacity) material.uniforms.uOpacity.value = opacity * (node.userData.baseOpacity ?? 1);
      if (material.uniforms.uProgress) material.uniforms.uProgress.value = progress;
      if (material.uniforms.uPpm) material.uniforms.uPpm.value = ppm;
    }
  });
}

/** A cupped helical strip with tapering ends; its silhouette has depth from every camera angle. */
export function spiralRibbon(radius: number, height: number, turns = 1, width = .12, phase = 0) {
  const positions: number[] = [], uv: number[] = [], indices: number[] = [];
  const segments = 80, across = 4;
  for (let i = 0; i <= segments; i++) {
    const t = i / segments, angle = phase + t * TAU * turns;
    const taper = Math.pow(Math.sin(t * Math.PI), .6);
    for (let j = 0; j <= across; j++) {
      const s = j / across, r = radius + Math.sin(s * Math.PI) * width * .4;
      positions.push(Math.cos(angle) * r, t * height + (s - .5) * width * taper, Math.sin(angle) * r); uv.push(t, s);
      if (i < segments && j < across) { const a = i * (across + 1) + j, b = a + across + 1; indices.push(a, b, a + 1, b, b + 1, a + 1); }
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3)); geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.setIndex(indices); geometry.computeVertexNormals(); return geometry;
}

/** Reusable world-space ribbon buffers, for travelling command pulses and electrical paths. */
export function pathRibbon(color: number, width = .07, mode = 2, accent = 0xffffff, segments = 36) {
  const geometry = new THREE.BufferGeometry();
  const positions = new THREE.Float32BufferAttribute(new Float32Array((segments + 1) * 2 * 3), 3);
  const uv: number[] = [], indices: number[] = [], normals: number[] = [];
  for (let i = 0; i <= segments; i++) {
    uv.push(i / segments, 0, i / segments, 1); normals.push(0, 1, 0, 0, 1, 0);
    if (i < segments) { const a = i * 2; indices.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
  }
  geometry.setAttribute('position', positions); geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3)); geometry.setIndex(indices);
  const mesh = new THREE.Mesh(geometry, effectMaterial(color, accent, mode)); mesh.frustumCulled = false;
  const sideways = new THREE.Vector3(), point = new THREE.Vector3();
  return { mesh, update(from: THREE.Vector3, to: THREE.Vector3, time: number, lightning = false, phase = 0, energy = 1) {
    const dx = to.x - from.x, dz = to.z - from.z, distance = Math.hypot(dx, dz) || 1;
    for (let i = 0; i <= segments; i++) {
      const t = i / segments, envelope = Math.sin(t * Math.PI), angle = t * TAU * 1.5 + phase + time * 2;
      const jitter = lightning ? Math.sin(i * 12.989 + Math.floor(time * 28) * 4.71 + phase) * .12 * energy * envelope : Math.sin(angle) * .09 * envelope;
      point.copy(from).lerp(to, t);
      point.x += -dz / distance * jitter; point.z += dx / distance * jitter;
      point.y += lightning ? Math.cos(i * 7.7 + Math.floor(time * 28)) * .08 * energy * envelope : envelope * .35;
      sideways.set(-dz / distance * Math.cos(angle), Math.sin(angle), dx / distance * Math.cos(angle)).multiplyScalar(width * (.25 + envelope * .75));
      positions.setXYZ(i * 2, point.x - sideways.x, point.y - sideways.y, point.z - sideways.z);
      positions.setXYZ(i * 2 + 1, point.x + sideways.x, point.y + sideways.y, point.z + sideways.z);
    }
    positions.needsUpdate = true;
  } };
}

/** Sparse soft particles; positions are functions of the effect clock, with no per-frame geometry allocation. */
export function effectParticles(color: number, count: number, radius: number, height: number, mode = 0) {
  const seeds: number[] = [], positions: number[] = [];
  for (let i = 0; i < count; i++) { positions.push(0, 0, 0); seeds.push((i + .5) / count, (i * .61803398875) % 1, (i * .41421356) % 1); }
  const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('aSeed', new THREE.Float32BufferAttribute(seeds, 3));
  const material = new THREE.ShaderMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false,
    uniforms: { uTime: { value: 0 }, uProgress: { value: 0 }, uOpacity: { value: 1 }, uPpm: { value: 40 },
      uColor: { value: new THREE.Color(color) }, uRadius: { value: radius }, uHeight: { value: height }, uMode: { value: mode } },
    vertexShader: `
      attribute vec3 aSeed;
      uniform float uTime; uniform float uProgress; uniform float uPpm; uniform float uRadius; uniform float uHeight; uniform float uMode;
      varying float vFade;
      void main() {
        float angle = aSeed.x * 6.283185 + uTime * (.45 + aSeed.z * .7);
        float rise = clamp(uProgress * 1.3 - aSeed.y * .3, 0.0, 1.0);
        float r = uRadius * (.5 + aSeed.z * .5);
        vec3 p = vec3(cos(angle) * r, rise * uHeight, sin(angle) * r);
        if (uMode > .5) p = vec3(cos(angle) * r, sin(angle * 1.3) * r, sin(angle) * r) * (.3 + uProgress);
        vFade = sin(rise * 3.14159) * (.5 + aSeed.z * .5);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        gl_PointSize = clamp(uPpm * (.045 + aSeed.z * .045), 1.5, 8.0);
      }`,
    fragmentShader: `
      uniform vec3 uColor; uniform float uOpacity; varying float vFade;
      void main() {
        float r = length(gl_PointCoord - .5) * 2.0;
        float alpha = pow(max(0.0, 1.0 - r), 2.0) * vFade * uOpacity;
        gl_FragColor = vec4(mix(uColor, vec3(1.0), .3), alpha);
      }`,
  });
  const particles = new THREE.Points(geometry, material); particles.frustumCulled = false; return particles;
}

/** Branching electrical filament with a fine core and translucent corona, aligned along local +Y. */
export function lightningVisual(length = .85) {
  const group = new THREE.Group(); group.name = 'lightning-projectile';
  const make = (points: THREE.Vector3[], radius: number, opacity: number) => {
    class KinkedPath extends THREE.Curve<THREE.Vector3> {
      constructor() { super(); }
      getPoint(t: number, target = new THREE.Vector3()) {
        const segment = Math.min(points.length - 2, Math.floor(t * (points.length - 1)));
        return target.copy(points[segment]).lerp(points[segment + 1], t * (points.length - 1) - segment);
      }
    }
    const geometry = new THREE.TubeGeometry(new KinkedPath(), 48, radius, 6, false);
    const mesh = new THREE.Mesh(geometry, effectMaterial(0x6aa9ff, 0xffffff, 4, .045));
    mesh.userData.baseOpacity = opacity; group.add(mesh);
  };
  const main = Array.from({ length: 9 }, (_, i) => new THREE.Vector3(i === 0 || i === 8 ? 0 : Math.sin(i * 8.3) * .13,
    (i / 8 - .5) * length, i === 0 || i === 8 ? 0 : Math.cos(i * 5.7) * .07));
  make(main, .044, 1); make(main, .11, .18);
  for (const side of [-1, 1]) {
    const branch = [main[side < 0 ? 3 : 5].clone(), new THREE.Vector3(side * .17, side * .08, .06), new THREE.Vector3(side * .28, side * .18, -.05)];
    make(branch, .009, .48); make(branch, .035, .12);
  }
  group.add(effectParticles(0xb6dcff, 8, .32, .2, 1)); return group;
}

export function disposeEffect(root: THREE.Object3D) {
  root.removeFromParent();
  const geometries = new Set<THREE.BufferGeometry>(), materials = new Set<THREE.Material>();
  root.traverse(node => {
    if (node instanceof THREE.Mesh || node instanceof THREE.Points || node instanceof THREE.Line) {
      geometries.add(node.geometry); for (const material of [node.material].flat()) materials.add(material);
    }
  });
  geometries.forEach(geometry => geometry.dispose()); materials.forEach(material => material.dispose());
}
